import { inject, Injectable } from '@angular/core';
import {
  catchError,
  concat,
  map,
  Observable,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { ActionOwner } from '../actions.model';
import {
  ACTION_EMPLOYEE_SEARCH_API,
  ActionEmployee,
} from './action-employee-search-api.token';

export interface ActionAssigneeSearchState {
  owners: ActionOwner[];
  loading: boolean;
  error: string | null;
}

/** Local results arrive first; remote lookup is bounded by a two-character minimum and debounce. */
@Injectable({ providedIn: 'root' })
export class ActionAssigneeSearchService {
  private readonly _employees = inject(ACTION_EMPLOYEE_SEARCH_API, {
    optional: true,
  });

  search(raw: string): Observable<ActionAssigneeSearchState> {
    const query = raw.trim();
    if (!query) return of({ owners: [], loading: false, error: null });
    if (!this._employees) {
      return of({
        owners: [],
        loading: false,
        error: 'Employee search is unavailable.',
      });
    }
    const employees = this._employees;
    return employees.findCachedEmployees(query).pipe(
      catchError(() => of([] as ActionEmployee[])),
      switchMap((cached) => {
        const owners = this._owners(cached);
        const local = { owners, loading: query.length >= 2, error: null };
        if (query.length < 2) return of(local);
        return concat(
          of(local),
          timer(300).pipe(
            switchMap(() => employees.queryEmployee(query)),
            map((response) => ({
              owners: this._owners([...response.payload.users, ...cached]),
              loading: false,
              error: null,
            })),
            catchError(() =>
              of({
                owners,
                loading: false,
                error:
                  'Employee search failed. You can select a cached match or try again.',
              }),
            ),
          ),
        );
      }),
    );
  }

  private _owners(employees: ActionEmployee[]): ActionOwner[] {
    const owners = new Map<string, ActionOwner>();
    for (const employee of employees) {
      if (!employee.id || owners.has(employee.id)) continue;
      owners.set(employee.id, {
        id: employee.id,
        displayName:
          [employee.firstName, employee.lastName]
            .filter(Boolean)
            .join(' ')
            .trim() ||
          employee.email ||
          employee.id,
        email: employee.email,
      });
    }
    return [...owners.values()].slice(0, 10);
  }
}
